import { useState, useRef, useEffect, useMemo, ChangeEvent, useId, FC, ReactNode } from 'react';
import { Search, X } from 'lucide-react';

export interface SearchBoxTemplateProps {
  children?: ReactNode; // flexible result content (single node, array, fragment)
  onSearchChange: (query: string) => void;
  placeholder?: string;
  defaultValue?: string;
  className?: string; // extra wrapper classes
  expandedWidthClass?: string; // reserves space to avoid layout shift (e.g. "tw-w-64")
  collapsedWidthClass?: string; // unfocused input width (e.g. "tw-w-28")
  alignment?: 'left' | 'right'; // "left" = expand right, "right" = expand left
  statusMessage?: string;
}

const SearchBoxTemplate: FC<SearchBoxTemplateProps> = ({
  children,
  onSearchChange,
  placeholder = 'Search',
  defaultValue = '',
  className = '',
  expandedWidthClass = 'tw-w-64',
  collapsedWidthClass = 'tw-w-36',
  alignment = 'right',
  statusMessage = '',
}) => {
  const [search, setSearch] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const baseId = useId();
  const listboxId = `search-results-${baseId}`;

  // propagate changes
  useEffect(() => {
    onSearchChange(search);
  }, [search, onSearchChange]);

  // close when clicking outside
  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      if (open && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  // escape closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!open) setOpen(true);
  };

  const clear = () => {
    setSearch('');
    inputRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className={`tw-relative tw-inline-block ${expandedWidthClass} ${className} ${
        alignment === 'right' ? 'tw-absolute tw-right-0' : ''
      }`}
    >
      <div className={`tw-flex tw-items-center ${alignment === 'right' ? 'tw-justify-end' : ''}`}>
        <div
          className={`tw-relative tw-transition-all tw-duration-200 tw-ease-in-out ${
            open ? expandedWidthClass : collapsedWidthClass
          } ${alignment === 'right' ? 'tw-flex tw-justify-end' : ''}`}
        >
          <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-z-2">
            <Search size={18} aria-hidden="true" />
          </div>
          <input
            ref={inputRef}
            id={`search-input-${baseId}`}
            type="text"
            aria-label="Search"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            placeholder={placeholder}
            value={search}
            onChange={handleChange}
            onFocus={() => setOpen(true)}
            className={`tw-w-full tw-rounded-full tw-border tw-border-gray-300 tw-py-2 tw-pl-10 tw-pr-10 tw-outline-none tw-transition-all tw-duration-200 tw-ease-in-out ${
              open
                ? 'tw-bg-white/80 tw-shadow-sm focus:tw-ring-2 focus:tw-ring-blue-500'
                : 'tw-bg-transparent hover:tw-bg-white/50 focus:tw-bg-white/80 focus:tw-ring-2 focus:tw-ring-blue-500'
            }`}
          />
          {search && (
            <div
              role="button"
              tabIndex={0}
              aria-label="Clear search"
              onClick={clear}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  clear();
                }
              }}
              className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-justify-center tw-w-10 tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors tw-duration-150 tw-cursor-pointer"
            >
              <X size={14} />
            </div>
          )}
        </div>
      </div>
      <div role="status" aria-live="polite" aria-atomic="true" className="tw-sr-only">
        {statusMessage}
      </div>
      {open && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Search results"
          className={`tw-absolute tw-mt-1 tw-z-10 tw-w-full tw-max-h-60 tw-overflow-y-auto tw-overscroll-contain tw-bg-white tw-shadow-md tw-rounded tw-border tw-border-gray-200 ${
            alignment === 'right' ? 'tw-right-0' : 'tw-left-0'
          }`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// source item with searchKey, id, and arbitrary node to render
export interface SourceItem {
  id: string;
  code: string;
  searchKey: string;
  node: React.ReactNode;
}

export interface SearchBoxProps {
  items: SourceItem[];
  onSelect?: (code: string) => void;
  placeholder?: string;
  noResultsNode?: React.ReactNode;
  showAllOnEmpty?: boolean; // if true, empty query shows all items; else shows none
  matchFn?: (query: string, key: string) => boolean; // default: case-insensitive substring
}

const defaultMatch: (query: string, key: string) => boolean = (q, key) => key.toLowerCase().includes(q.toLowerCase());

const defaultNoResultsNode: React.ReactNode = (
  <p key="no-results" className="tw-text-gray-500 tw-text-sm tw-font-atkinson tw-m-4 tw-text-center">
    No results found.
  </p>
);

const SearchBox: FC<SearchBoxProps> = ({
  items,
  onSelect,
  placeholder,
  noResultsNode = defaultNoResultsNode,
  showAllOnEmpty = true,
  matchFn = defaultMatch,
}) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo<SourceItem[]>(() => {
    const trimmed = query.trim();
    if (!trimmed && !showAllOnEmpty) return [];
    if (!trimmed && showAllOnEmpty) return items;
    return items.filter(it => matchFn(trimmed, it.searchKey));
  }, [query, items, matchFn, showAllOnEmpty]);

  const statusMessage = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return '';
    if (filtered.length === 0) {
      return 'No results found.';
    }
    return `${filtered.length} search result${filtered.length === 1 ? '' : 's'} available.`;
  }, [query, filtered.length]);

  // Announce the status only after the user stops typing, so the polite live
  // region doesn't cut off NVDA's keystroke/word echo mid-word.
  const [announcedMessage, setAnnouncedMessage] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setAnnouncedMessage(statusMessage), 1200);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  const resultNodes = useMemo<React.ReactNode[]>(
    () =>
      filtered.length > 0
        ? filtered.map(it => (
            <div
              key={it.id}
              tabIndex={onSelect ? 0 : undefined}
              onKeyDown={e => {
                if (!onSelect) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(it.code);
                }
              }}
              role={onSelect ? 'option' : undefined}
              className="tw-p-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => {
                if (onSelect) onSelect(it.code);
              }}
              data-search-code={it.code}
              aria-label={it.searchKey}
            >
              {it.node}
            </div>
          ))
        : [noResultsNode],
    [filtered, onSelect],
  );

  return (
    <SearchBoxTemplate placeholder={placeholder} onSearchChange={setQuery} statusMessage={announcedMessage}>
      {resultNodes}
    </SearchBoxTemplate>
  );
};

export default SearchBox;
